import { calculateWorkOrderCosts } from '@calculations/cost/work-order';
import { calculate } from '@calculations/price/strategies/raf-fixed-market/calculate';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { noOpUnitPrice } from '@ui/features/quote-builder/state/calculations/strategies/no-op';
import { QuoteLineType } from '@ui/features/quote-builder/types/state/quote-line';
import { QuotePriceType } from '@ui/features/quote-builder/types/state/quote-price';

import { storeToCalculationSnapWorkOrder } from '../../transforms/store-to-calculation-snap-work-order';
import { transformWorkOrderCostsToStoreState } from '../../utilities/transform-work-order-costs';
import {
  OnQuantityChangeInput,
  OnQuantityChangeOutput,
  OnQuotePriceCreationInput,
  OnQuotePriceCreationOutput,
  RecalculatePriceStrategy,
} from '../types';
import { getAllowFallback } from '../utilities/get-allow-fallback';

function onQuantityChange(
  inputs: OnQuantityChangeInput,
): OnQuantityChangeOutput {
  const { state, quoteLine, quotePrice, quantity, priceStrategyConfigs } =
    inputs;

  if (!state.marketCode) {
    return {
      status: 'failure',
      reason: 'No market code found',
    };
  }

  if (!quoteLine.marketPrices) {
    return {
      status: 'failure',
      reason: 'No market prices found',
    };
  }

  if (!quoteLine.craftProductOptions) {
    return {
      status: 'failure',
      reason: 'No craft product options found',
    };
  }

  const allowFallback = getAllowFallback(
    quotePrice.priceStrategy,
    priceStrategyConfigs,
  );

  const output = calculate(
    {
      quantity,
      allowFallback,
      marketPrices: quoteLine.marketPrices,
      marketCode: state.marketCode,
      productOptions: quoteLine.productOptionsUuids.map(
        (uuid) => quoteLine.productOptions[uuid],
      ),
      craftProductOptions: quoteLine.craftProductOptions,
    },
    SnapRoundingOptionsCustom(state),
  );

  if (!output) {
    return {
      status: 'failure',
      reason: 'Price calculation failed',
    };
  }

  let costData:
    | ReturnType<typeof transformWorkOrderCostsToStoreState>
    | undefined;

  if (quoteLine.snapWorkOrder) {
    const costs = calculateWorkOrderCosts({
      quantity,
      ...storeToCalculationSnapWorkOrder(quoteLine.snapWorkOrder),
    });
    costData = transformWorkOrderCostsToStoreState(costs);
  }

  return {
    status: 'success',
    unitPrice: output.unitPrice,
    unitPriceAddition: output.unitPriceAddition,
    changesUnitPrice: output.changesUnitPrice,
    ...costData,
  };
}

function onQuotePriceCreation(
  inputs: OnQuotePriceCreationInput,
): OnQuotePriceCreationOutput {
  const { state, quoteLine, quotePrice, priceStrategyConfigs } = inputs;

  if (!state.marketCode) {
    return {
      status: 'failure',
      reason: 'No market code found',
    };
  }

  if (!quoteLine.marketPrices) {
    return {
      status: 'failure',
      reason: 'No market prices found',
    };
  }

  if (!quoteLine.craftProductOptions) {
    return {
      status: 'failure',
      reason: 'No craft product options found',
    };
  }

  const allowFallback = getAllowFallback(
    quotePrice.priceStrategy,
    priceStrategyConfigs,
  );

  const output = calculate(
    {
      quantity: quotePrice.quantity,
      allowFallback,
      marketPrices: quoteLine.marketPrices,
      marketCode: state.marketCode,
      productOptions: quoteLine.productOptionsUuids.map(
        (uuid) => quoteLine.productOptions[uuid],
      ),
      craftProductOptions: quoteLine.craftProductOptions,
    },
    SnapRoundingOptionsCustom(state),
  );

  if (!output) {
    return {
      status: 'failure',
      reason: 'Price calculation failed',
    };
  }

  return {
    status: 'success',
    quotePrice: {
      ...quotePrice,
      unitPrice: output.unitPrice,
      ...getQuotePriceCostData(quoteLine, quotePrice),
    },
  };
}

function getQuotePriceCostData(
  quoteLine: QuoteLineType,
  quotePrice: QuotePriceType,
): ReturnType<typeof transformWorkOrderCostsToStoreState> | undefined {
  if (!quoteLine.snapWorkOrder) {
    return;
  }

  const costs = calculateWorkOrderCosts({
    quantity: quotePrice.quantity,
    ...storeToCalculationSnapWorkOrder(quoteLine.snapWorkOrder),
  });

  return transformWorkOrderCostsToStoreState(costs);
}

export const recalculateRAFFixedMarket = {
  onQuantityChange,
  onUnitPriceChange: noOpUnitPrice,
  onGrossMarginChange: null,
  onContributionMarginChange: null,
  onBaseGrossMarginChange: null,
  onQuotePriceCreation,
  onUnitCostChange: null,
} satisfies RecalculatePriceStrategy;
